import { useState } from "react";
import { Download, FileImage, FileText } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default function ExportButton({ targetRef, filename = "export", title = "Export" }) {
  const [isExporting, setIsExporting] = useState(false);

  const exportAsImage = async () => {
    if (!targetRef?.current) {
      toast.error("Nothing to export");
      return;
    }

    setIsExporting(true);
    try {
      const element = targetRef.current;
      const canvas = await html2canvas(element, {
        backgroundColor: "#050505",
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const link = document.createElement("a");
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      
      toast.success("Image downloaded!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export image");
    } finally {
      setIsExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!targetRef?.current) {
      toast.error("Nothing to export");
      return;
    }

    setIsExporting(true);
    try {
      const element = targetRef.current;
      const canvas = await html2canvas(element, {
        backgroundColor: "#050505",
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Calculate PDF dimensions (landscape or portrait based on content)
      const isLandscape = imgWidth > imgHeight;
      const pdf = new jsPDF({
        orientation: isLandscape ? "landscape" : "portrait",
        unit: "px",
        format: [imgWidth / 2, imgHeight / 2],
      });

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth / 2, imgHeight / 2);
      pdf.save(`${filename}.pdf`);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isExporting}
          className="bg-white/5 border-white/20 text-white hover:bg-white/10"
          data-testid="export-button"
        >
          <Download className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">{isExporting ? "Exporting..." : title}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#111] border-white/20">
        <DropdownMenuItem
          onClick={exportAsImage}
          className="text-white hover:bg-white/10 cursor-pointer"
          data-testid="export-image-btn"
        >
          <FileImage className="w-4 h-4 mr-2" />
          Download as PNG
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={exportAsPDF}
          className="text-white hover:bg-white/10 cursor-pointer"
          data-testid="export-pdf-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Download as PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
